"use client"

import { useEffect, useRef, useState } from "react"

import { cn } from "@productlane/lib/src/cn"

import type { AgentOrbColors, AgentOrbState } from "./orb-math"
import type { OrbHandle } from "./orb-renderer"
import { registerOrb } from "./orb-renderer"
import { useAccentOrbColors } from "./use-accent-orb-colors"

export type { AgentOrbColors, AgentOrbState } from "./orb-math"
export { agentOrbColorsFromAccent, DEFAULT_AGENT_ORB_COLORS } from "./orb-math"

/** The CSS stand-in before the first frame, and the whole orb where WebGL2 is missing. */
export function agentOrbFallback([accent, light, tint]: AgentOrbColors) {
	return `radial-gradient(circle at 65% 30%, #ffffff 0%, ${light} 30%, ${accent} 70%, ${tint} 100%)`
}

/** The AI agent's avatar: a lit gradient sphere in the portal accent whose motion follows what the agent is doing. */
export function AgentOrb({
	size = 20,
	state = "idle",
	colors,
	seed = 0,
	className,
	title,
}: {
	size?: number
	state?: AgentOrbState
	colors?: AgentOrbColors
	seed?: number
	className?: string
	title?: string
}) {
	const accentColors = useAccentOrbColors()
	const [accent, light, tint] = colors ?? accentColors
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const handleRef = useRef<OrbHandle | null>(null)
	const [drawn, setDrawn] = useState(false)
	// The server render cannot read the page's accent, so the orb waits for the
	// client's first render: painting the default first flashed blue, then green.
	const [mounted, setMounted] = useState(false)
	useEffect(() => setMounted(true), [])
	// Browser zoom raises the pixel density; the orb re-renders at the new density.
	const [dpr, setDpr] = useState(1)
	useEffect(() => {
		if (typeof window === "undefined" || !window.matchMedia) return
		setDpr(window.devicePixelRatio || 1)
		const query = window.matchMedia(
			`(resolution: ${window.devicePixelRatio}dppx)`,
		)
		const onChange = () => setDpr(window.devicePixelRatio || 1)
		query.addEventListener?.("change", onChange)
		return () => query.removeEventListener?.("change", onChange)
	}, [dpr])

	useEffect(() => {
		const canvas = canvasRef.current
		if (!canvas || !mounted) return
		const handle = registerOrb(canvas, {
			size,
			state,
			colors: [accent, light, tint],
			seed,
			onFirstDraw: () => setDrawn(true),
		})
		handleRef.current = handle
		return () => {
			handle?.dispose()
			handleRef.current = null
		}
		// state and colors flow through update() below, so a change never re-registers.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [size, seed, mounted, dpr])

	useEffect(() => {
		handleRef.current?.update({ state, colors: [accent, light, tint] })
	}, [state, accent, light, tint])

	return (
		<span
			role={title ? "img" : undefined}
			aria-label={title}
			aria-hidden={title ? undefined : true}
			title={title}
			data-agent-orb={state}
			className={cn("relative inline-flex flex-none rounded-full", className)}
			style={{
				width: size,
				height: size,
				opacity: mounted ? 1 : 0,
				transition: "opacity 150ms cubic-bezier(0.23, 1, 0.32, 1)",
				// Off once the canvas paints, or it shows as a ring round the breathing sphere.
				background:
					mounted && !drawn
						? agentOrbFallback([accent, light, tint])
						: undefined,
			}}
		>
			<canvas
				ref={canvasRef}
				className="absolute inset-0 block"
				style={{ width: size, height: size }}
			/>
		</span>
	)
}
